/**
 * useFeatureFlags Hook
 *
 * Provides access to global feature flag states from the server.
 * Caches the config for the session to avoid repeated API calls.
 *
 * Security Note: This hook uses fail-open behavior on network errors
 * (shows features when flag state is unknown). This is safe because:
 * 1. The backend middleware uses fail-CLOSED behavior (denies access on errors)
 * 2. The backend is the source of truth and enforces feature flags on all API calls
 * 3. Frontend state only affects UI visibility, not actual feature access
 *
 * This defense-in-depth approach provides good UX (no flash of "unavailable")
 * while maintaining security (backend enforces regardless of frontend state).
 *
 * @example
 * const { classManagementEnabled, isLoading } = useFeatureFlags();
 * if (classManagementEnabled) {
 *   // Show class management UI
 * }
 *
 * @see server/middleware/featureFlagMiddleware.js - Fail-closed middleware
 * @see server/services/EntitlementService.js - Flag definitions and caching
 */

import { useState, useEffect, useCallback, useRef } from 'react'

import { createLogger } from '../utils/logger'

const logger = createLogger('useFeatureFlags')

/**
 * Default feature flag values.
 *
 * These defaults serve as:
 * 1. Initial state before API fetch completes
 * 2. Fallback values when API fetch fails (fail-open behavior)
 * 3. Source of truth for frontend default assumptions
 *
 * IMPORTANT: Keep in sync with server-side flag defaults in:
 * - server/services/EntitlementService.js (DEFINED_FEATURE_FLAGS)
 * - server/routes/config.js (public config endpoint)
 *
 * Test Usage: Import DEFAULT_FLAGS in tests to verify components
 * handle both enabled/disabled states correctly.
 *
 * @constant {Object}
 * @property {boolean} registrationEnabled - Whether new user registration is open
 * @property {boolean} classManagementEnabled - Whether class/LMS features are available
 */
export const DEFAULT_FLAGS = {
  registrationEnabled: true,
  classManagementEnabled: true
}

/**
 * Module-level cache to persist across component instances.
 *
 * IMPORTANT: This state persists across Hot Module Replacement (HMR) during
 * development. If you change feature flag values in the admin UI during dev,
 * you may need to refresh the page to see the changes reflected in the UI.
 * In production, this is not an issue since there's no HMR.
 *
 * @private
 */
let cachedConfig = null
let cachePromise = null
let retryCount = 0

/**
 * Retry configuration for exponential backoff
 * Delays: 1s, 2s, 4s (RETRY_DELAY_MS * 2^(attempt-1))
 */
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000
const BACKOFF_MULTIPLIER = 2

/**
 * Delay helper for retry logic
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Fetch feature flags with retry logic and exponential backoff.
 * Uses promise deduplication to prevent multiple concurrent requests.
 *
 * @param {boolean} isRetry - Whether this is a retry attempt (internal use)
 * @returns {Promise<Object>} The feature flags config object
 */
const fetchConfig = async (_isRetry = false) => {
  if (cachedConfig) return cachedConfig

  // Deduplicate concurrent requests
  if (cachePromise) return cachePromise

  // Store reference to this promise for race condition prevention
  const currentPromise = (async () => {
    try {
      const res = await fetch('/api/config/public', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`)
      }

      const config = await res.json()
      cachedConfig = config
      retryCount = 0 // Reset retry count on success
      return config
    } catch (error) {
      logger.error('Failed to fetch feature flags:', error)

      // Retry with exponential backoff
      if (retryCount < MAX_RETRIES) {
        retryCount++
        const backoffDelay = RETRY_DELAY_MS * Math.pow(BACKOFF_MULTIPLIER, retryCount - 1)
        logger.debug(
          `Retrying feature flag fetch in ${backoffDelay}ms (attempt ${retryCount}/${MAX_RETRIES})`
        )
        await delay(backoffDelay)
        cachePromise = null // Clear promise to allow retry
        return fetchConfig(true)
      }

      // Return defaults on final failure (fail-open matches backend behavior)
      // Note: Backend enforces feature flags regardless of frontend state
      logger.warn('Feature flag fetch failed after retries, using defaults')
      return { ...DEFAULT_FLAGS }
    } finally {
      // Only clear promise if this is still the current promise
      // This prevents race conditions where a retry has already set a new promise
      if (cachePromise === currentPromise) {
        cachePromise = null
      }
    }
  })()

  cachePromise = currentPromise
  return cachePromise
}

export const useFeatureFlags = () => {
  const [flags, setFlags] = useState({
    ...DEFAULT_FLAGS,
    isLoading: true,
    fetchError: false
  })
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    fetchConfig().then((config) => {
      if (mountedRef.current) {
        setFlags({
          ...config,
          isLoading: false,
          fetchError: config === DEFAULT_FLAGS
        })
      }
    })

    return () => {
      mountedRef.current = false
    }
  }, [])

  const refresh = useCallback(async () => {
    cachedConfig = null
    retryCount = 0
    const config = await fetchConfig()
    if (mountedRef.current) {
      setFlags({ ...config, isLoading: false, fetchError: false })
    }
  }, [])

  return { ...flags, refresh }
}

// Imperative API for non-hook contexts
export const getFeatureFlags = () => fetchConfig()
export const clearFeatureFlagCache = () => {
  cachedConfig = null
  retryCount = 0
}

/**
 * Reset module state for test isolation.
 * This function should only be called in test setup/teardown.
 * @private
 */
export const __resetForTesting = () => {
  cachedConfig = null
  cachePromise = null
  retryCount = 0
}

export default useFeatureFlags
